import React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Image,
  Container,
  ListGroup,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import Meta from "../components/Meta";
import {
  useSubmitPartMutation,
  // useUploadPartSubmissionFileMutation, // file upload commented out - uncomment to revert
} from "../slices/submissionApiSlice";
import Loader from "../components/Loader";
import { validateForm, formatPhoneNumber } from "../utils/formValidation";
import RecaptchaCheckbox from "../components/RecaptchaCheckbox";

const PartScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [partType, setPartType] = useState("");
  const [details, setDetails] = useState("");
  // const [file, setFile] = useState(null); // file upload commented out for easy revert
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const recaptchaRef = useRef(null);

  const [submitPart, { isLoading: isSubmitting }] = useSubmitPartMutation();
  // const [uploadPartSubmissionFile, { isLoading: isUploading }] =
  //   useUploadPartSubmissionFileMutation(); // file upload commented out for easy revert

  useEffect(() => {
    if (location.hash !== "#inquiry") return;
    const id = setTimeout(() => {
      const el = document.getElementById("inquiry");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(id);
  }, [location.pathname, location.hash]);

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    // Skip validation for email field - let browser handle it
    if (field !== "email") {
      validateField(field);
    }
  };

  const validateField = (field) => {
    const formData = { name, email, phone, subject, partType, details };
    const validation = validateForm(formData, "parts");
    setErrors((prev) => ({
      ...prev,
      [field]: validation.errors[field] || null,
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    if (touched.phone) {
      validateField("phone");
    }
  };

  const handlePhoneBlur = () => {
    setTouched({ ...touched, phone: true });
    // Format phone number on blur
    const formatted = formatPhoneNumber(phone);
    setPhone(formatted);
    validateField("phone");
  };

  /* file upload commented out for easy revert
  const uploadFileHandler = async (e) => {
    const fileData = new FormData();
    fileData.append("file", e.target.files[0]);
    fileData.append("formType", "part");
    try {
      const uploadResponse = await uploadPartSubmissionFile(fileData).unwrap();
      toast.success("File uploaded successfully!");
      setFile(uploadResponse.file);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };
  */

  const submitHandler = async (e) => {
    e.preventDefault();

    const formData = {
      name,
      email,
      phone: phone.replace(/\D/g, ""), // Store only digits
      subject,
      partType,
      details,
      file: null, // file upload commented out; was: file
    };

    // Validate entire form
    const validation = validateForm(formData, "parts");

    if (!validation.isValid) {
      setErrors(validation.errors);
      setTouched({
        name: true,
        email: true,
        phone: true,
        subject: true,
        partType: true,
        details: true,
      });
      toast.error("Please correct the errors in the form");
      return;
    }

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA checkbox");
      return;
    }

    try {
      await submitPart({ ...formData, recaptchaToken }).unwrap();
      recaptchaRef.current?.reset();
      setRecaptchaToken("");
      toast.success("Submitting form...");
      navigate("/parts");
      toast.success("Form submitted successfully!");
      // Clear the form
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setPartType("");
      setDetails("");
      // setFile(null); // file upload commented out for easy revert
      setErrors({});
      setTouched({});
    } catch (err) {
      recaptchaRef.current?.reset();
      setRecaptchaToken("");
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <Meta
        title="Parts | Lugo Parts & Restoration"
        description="Are you in search of a specific part for your GMC restoration project? From engines and interiors to custom pieces, we provide authentic and high-quality components to meet your needs."
        keywords="Parts, Catalog, Classic GMC Trucks, Engines, Interiors, Custom Pieces, Authentic, High-Quality, Restoration, Vintage, Automotive, Shopping, Online, Secure, Reliable, Wide Range, Exclusive, Customer Service, Quality Assurance"
      />

      <Container>
        <Alert
          variant="info"
          className="mt-4 mb-4"
          style={{ borderLeft: "4px solid #d80607" }}
        >
          <Alert.Heading style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
            Looking for a specific part?
          </Alert.Heading>
          <p className="mb-0" style={{ fontSize: "0.95rem" }}>
            If you'd like to inquire about rare or restorable parts, you can
            find our submission form at the bottom of this page.
          </p>
        </Alert>
        {/* Parts Section */}
        <section className="about-me mt-5 mb-5">
          <h3 className="mb-5">Your Treasure Trove for Vintage Truck Parts</h3>
          <Row className="align-items-center">
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    Discover a World of Possibilities at Lugo Parts - Your
                    Ultimate Source for 1955-1959 Chevrolet and GMC Truck Parts!
                    Immerse yourself in a treasure trove of new and used
                    components meticulously curated for classic enthusiasts.
                    <br />
                    <br />
                  </p>
                  <p className="landing-page-text">
                    Unearth a wide selection of top-quality parts that breathe
                    life into your restoration dreams. From authentic OEM
                    components to carefully sourced pre-loved treasures, our
                    inventory spans the spectrum to cater to every restoration
                    project. At Lugo Parts, we're not only dedicated to
                    providing the highest caliber parts, but we're also
                    committed to making them accessible by offering competitive
                    pricing.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
            <Col md={6}>
              <Image src="/images/compressed/webp/Parts01.webp" fluid rounded />
            </Col>
          </Row>
        </section>

        {/* Parts Brand Section */}
        <section className="certified-parts mt-5 mb-5">
          <h3 className="mb-5">Certified Parts and Trusted Brands</h3>
          <Row className="align-items-center">
            <Col md={9}>
              <p className="landing-page-text">
                At Lugo Parts & Restoration, we take pride in offering parts
                that are not just high-quality but also certified by trusted
                brands. Our range of parts comes with the assurance of quality,
                backed by the reputation of brands like NAPCO, Chevrolet, and
                GMC.
                <br />
                <br />
                Explore our collection of certified parts and take your
                restoration project to the next level with components you can
                trust. We understand that the success of your restoration
                journey hinges on the quality and authenticity of the parts you
                use. That's why we go the extra mile to source parts that meet
                stringent quality standards.
                <br />
                <br />
                With Lugo Parts & Restoration, you're not just buying parts;
                you're investing in quality, reliability, and the ultimate
                success of your restoration project.
              </p>
            </Col>
          </Row>
          {/* For Sticker Desktop View */}
          <Row className="align-items-center mt-4 justify-content-between d-none d-md-flex">
            <Col xs={6} md={4}>
              <Image
                src="/images/compressed/stickers/webp/GMCinMotor.webp"
                fluid
              />
            </Col>
            <Col xs={6} md={4}>
              <Image
                src="/images/compressed/stickers/webp/ChevroletBigWheel.webp"
                fluid
              />
            </Col>
            <Col xs={6} md={4}>
              <Image src="/images/compressed/stickers/GMCcoach.png" fluid />
            </Col>
          </Row>
          <Row className="align-items-center mt-4 justify-content-between d-none d-md-flex">
            <Col xs={6} md={4}>
              <div className="d-flex justify-content-center">
                <Image
                  src="/images/compressed/stickers/webp/NapcoNewSize.webp"
                  fluid
                />
              </div>
            </Col>
            <Col xs={6} md={4}>
              <div className="d-flex justify-content-center">
                <Image
                  src="/images/compressed/stickers/webp/FleetsideNapco.webp"
                  fluid
                />
              </div>
            </Col>
            <Col xs={6} md={4}>
              <div className="d-flex justify-content-center">
                <Image
                  src="/images/compressed/stickers/webp/BlueChip1.webp"
                  fluid
                />
              </div>
            </Col>
          </Row>

          {/* For Sticker Mobile View */}
          <Row className="align-items-center mt-4 justify-content-between d-md-none d-flex flex-column">
            <Col className="mb-2">
              <Image
                src="/images/compressed/stickers/webp/GMCinMotor.webp"
                fluid
              />
            </Col>

            <Col className="mb-2">
              <Image
                src="/images/compressed/stickers/webp/GMCcoach.webp"
                fluid
              />
            </Col>
            <Col className="mb-2">
              <div className="d-flex justify-content-center">
                <Image
                  src="/images/compressed/stickers/webp/FleetsideNapco.webp"
                  fluid
                />
              </div>
            </Col>
            <Col className="mb-2">
              <Image
                src="/images/compressed/stickers/webp/ChevroletBigWheel.webp"
                fluid
              />
            </Col>
            <Col className="mb-2">
              <div className="d-flex justify-content-center">
                <Image
                  src="/images/compressed/stickers/webp/NapcoNewSize.webp"
                  fluid
                />
              </div>
            </Col>
            <Col className="mb-2">
              <div className="d-flex justify-content-center">
                <Image
                  src="/images/compressed/stickers/webp/BlueChip1.webp"
                  fluid
                />
              </div>
            </Col>
          </Row>
        </section>

        {/* Parts Inquiry Section */}
        <section id="inquiry" className="custom-inquiry mt-5 mb-5">
          <h3 className="mb-5" style={{ textAlign: "center" }}>
            Inquire About Rare and Restorable Truck Parts
          </h3>
          <Row className="align-items-center">
            <Col md={12}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    Step into a world where classic truck restoration
                    aspirations become reality.
                    <br />
                    <br />
                    Fill out the form below with your specific needs, and our
                    dedicated team will guide you through your selection,
                    ensuring a seamless experience from start to finish.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
          <Form onSubmit={submitHandler}>
            <Form.Group controlId="name" className="my-3">
              <Form.Label>Your Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleBlur("name")}
                className={errors.name && touched.name ? "is-invalid" : ""}
                required
              />
              {errors.name && touched.name && (
                <div className="form-error">{errors.name}</div>
              )}
            </Form.Group>

            <Form.Group controlId="email" className="my-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur("email")}
                required
              />
            </Form.Group>

            <Form.Group controlId="phone" className="my-3">
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter your phone number (with area code)"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                className={errors.phone && touched.phone ? "is-invalid" : ""}
                required
              />
              {errors.phone && touched.phone && (
                <div className="form-error">{errors.phone}</div>
              )}
              <Form.Text className="text-muted">
                Include area code (e.g., 555-123-4567)
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="subject" className="my-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter the subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onBlur={() => handleBlur("subject")}
                className={
                  errors.subject && touched.subject ? "is-invalid" : ""
                }
                required
              />
              {errors.subject && touched.subject && (
                <div className="form-error">{errors.subject}</div>
              )}
            </Form.Group>

            <Form.Group controlId="partType" className="my-3">
              <Form.Label>Type of Part *</Form.Label>
              <Form.Select
                value={partType}
                onChange={(e) => setPartType(e.target.value)}
                onBlur={() => handleBlur("partType")}
                className={
                  errors.partType && touched.partType ? "is-invalid" : ""
                }
                required
              >
                <option value="">Select Type of Part</option>
                <option value="Rare">Rare</option>
                <option value="Restorable Used Item">
                  Restorable Used Item
                </option>
                <option value="OEM">OEM</option>
              </Form.Select>
              {errors.partType && touched.partType && (
                <div className="form-error">{errors.partType}</div>
              )}
            </Form.Group>

            <Form.Group controlId="details" className="my-3">
              <Form.Label>Part Details *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Describe the specific part(s) you are inquiring about..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                onBlur={() => handleBlur("details")}
                className={
                  errors.details && touched.details ? "is-invalid" : ""
                }
                required
              />
              {errors.details && touched.details && (
                <div className="form-error">{errors.details}</div>
              )}
            </Form.Group>

            {/* file upload commented out for easy revert
            <Form.Group controlId="file" className="my-3">
              <Form.Label>File Upload (PDF)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={uploadFileHandler}
              />
            </Form.Group>
            */}

            <div
              className="my-3 p-3 rounded"
              style={{
                backgroundColor: "#f8f9fa",
                borderLeft: "4px solid #d80607",
                color: "#1a1a1a",
              }}
            >
              <strong style={{ fontSize: "1rem", color: "#1a1a1a" }}>
                Submit photos of the parts
              </strong>
              <p
                className="mb-0 mt-2"
                style={{
                  fontSize: "1rem",
                  lineHeight: 1.5,
                  color: "#1a1a1a",
                }}
              >
                Please email{" "}
                <a
                  href="mailto:lugoparts@yahoo.com"
                  style={{
                    color: "#0d6efd",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  lugoparts@yahoo.com
                </a>{" "}
                or text{" "}
                <a
                  href="tel:916-652-0840"
                  style={{
                    color: "#0d6efd",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  916-652-0840
                </a>{" "}
                with photos of the parts (images only) and your first and last
                name.
              </p>
            </div>

            <RecaptchaCheckbox
              ref={recaptchaRef}
              action="part_submit"
              onTokenChange={setRecaptchaToken}
            />

            <Button
              type="submit"
              variant="primary"
              className="mt-2"
              disabled={isSubmitting}
            >
              Submit Inquiry
            </Button>
            {isSubmitting && <Loader />}
          </Form>
        </section>
      </Container>
    </>
  );
};

export default PartScreen;
